import { Injectable } from '@angular/core';
import { uuid } from 'jakapa-utilities-lite';
import { LeaderReportFilters } from './common';
import {
	LeaderReportAPIResponseGrowth,
	LeaderReportGrowthService,
} from './growth.service';
import { LeaderReportOverallScoresService } from './overall-scores.service';
import {
	LeaderReportAPIResponseSelfAwareness,
	LeaderReportSelfAwarenessService,
} from './self-awareness.service';
import {
	LeaderReportAPIResponseSelfByGroup,
	LeaderReportSelfByGroupService,
} from './self-by-group.service';
import {
	LeaderReportAPIResponseSelfVsPeerBySkill,
	LeaderReportSelfVsPeerBySkillService,
} from './self-vs-peer-by-skill.service';
import {
	LeaderReportAPIResponseSelfVsPeerByUser,
	LeaderReportSelfVsPeerByUserService,
} from './self-vs-peer-by-user.service';
import {
	LeaderReportAPIResponseStrengthsAndWeaknesses,
	LeaderReportStrengthsAndWeaknessesService,
} from './strengths-and-weaknesses.service';

export enum LeaderReportType {
	DOMAIN_GROWTH = 'domainGrowth',
	DOMAIN_OVERALL_SCORES = 'domainOverallScores',
	SELF_AWARENESS = 'selfAwareness',
	SELF_BY_GROUP = 'selfByGroup',
	SELF_VS_PEER_BY_SKILL = 'selfVsPeerBySkill',
	SELF_VS_PEER_BY_USER = 'selfVsPeerByUser',
	SKILL_GROWTH = 'skillGrowth',
	SKILL_OVERALL_SCORES = 'skillOverallScores',
	STRENGTHS_AND_WEAKNESSES = 'strengthsAndWeaknesses',
}

export interface LeaderReportAPIResponse {
	[LeaderReportType.DOMAIN_GROWTH]?: Array<LeaderReportAPIResponseGrowth>;
	[LeaderReportType.DOMAIN_OVERALL_SCORES]?: Array<Array<uuid>>;
	[LeaderReportType.SELF_AWARENESS]?: Array<LeaderReportAPIResponseSelfAwareness>;
	[LeaderReportType.SELF_BY_GROUP]?: Array<LeaderReportAPIResponseSelfByGroup>;
	[LeaderReportType.SELF_VS_PEER_BY_SKILL]?: Array<LeaderReportAPIResponseSelfVsPeerBySkill>;
	[LeaderReportType.SELF_VS_PEER_BY_USER]?: Array<LeaderReportAPIResponseSelfVsPeerByUser>;
	[LeaderReportType.SKILL_GROWTH]?: Array<LeaderReportAPIResponseGrowth>;
	[LeaderReportType.SKILL_OVERALL_SCORES]?: Array<Array<uuid>>;
	[LeaderReportType.STRENGTHS_AND_WEAKNESSES]?: Array<LeaderReportAPIResponseStrengthsAndWeaknesses>;
}

@Injectable({
	providedIn: 'root',
})
export class LeaderReportInitializationService {
	private _REPORT_INITIALIZATION: {
		[key in LeaderReportType]: {
			filters: Array<keyof LeaderReportFilters>;
			generator: (data: any, viewAll: boolean, ...args: any) => any;
		};
	};

	constructor(
		private _growthSvc: LeaderReportGrowthService,
		private _overallScoresSvc: LeaderReportOverallScoresService,
		private _selfAwarenessSvc: LeaderReportSelfAwarenessService,
		private _selfByGroupSvc: LeaderReportSelfByGroupService,
		private _selfVsPeerBySkillSvc: LeaderReportSelfVsPeerBySkillService,
		private _selfVsPeerByUserSvc: LeaderReportSelfVsPeerByUserService,
		private _strengthsAndWeaknessesSvc: LeaderReportStrengthsAndWeaknessesService,
	) {
		this._REPORT_INITIALIZATION = {
			[LeaderReportType.DOMAIN_GROWTH]: {
				filters: ['domain', 'organization', 'group', 'timespan', 'user'],
				generator: this._growthSvc.generateReport.bind(this._growthSvc),
			},
			[LeaderReportType.DOMAIN_OVERALL_SCORES]: {
				filters: ['domain', 'organization', 'group', 'timespan', 'user'],
				generator: this._overallScoresSvc.generateReport.bind(
					this._overallScoresSvc,
				),
			},
			[LeaderReportType.SELF_AWARENESS]: {
				filters: ['domain', 'organization', 'group', 'timespan', 'user'],
				generator: this._selfAwarenessSvc.generateReport.bind(
					this._selfAwarenessSvc,
				),
			},
			[LeaderReportType.SELF_BY_GROUP]: {
				filters: ['domain', 'organization', 'timespan', 'viewAll'],
				generator: this._selfByGroupSvc.generateReport.bind(
					this._selfByGroupSvc,
				),
			},
			[LeaderReportType.SELF_VS_PEER_BY_SKILL]: {
				filters: ['domain', 'organization', 'assessment', 'group', 'user'],
				generator: this._selfVsPeerBySkillSvc.generateReport.bind(
					this._selfVsPeerBySkillSvc,
				),
			},
			[LeaderReportType.SELF_VS_PEER_BY_USER]: {
				filters: [
					'domain',
					'organization',
					'group',
					'timespan',
					'user',
					'viewAll',
				],
				generator: this._selfVsPeerByUserSvc.generateReport.bind(
					this._selfVsPeerByUserSvc,
				),
			},
			[LeaderReportType.SKILL_GROWTH]: {
				filters: [
					'domain',
					'organization',
					'assessment',
					'group',
					'skill',
					'user',
				],
				generator: this._growthSvc.generateReport.bind(this._growthSvc),
			},
			[LeaderReportType.SKILL_OVERALL_SCORES]: {
				filters: [
					'domain',
					'organization',
					'assessment',
					'group',
					'skill',
					'user',
				],
				generator: this._overallScoresSvc.generateReport.bind(
					this._overallScoresSvc,
				),
			},
			[LeaderReportType.STRENGTHS_AND_WEAKNESSES]: {
				filters: [
					'domain',
					'organization',
					'assessment',
					'group',
					'timespan',
					'user',
				],
				generator: this._strengthsAndWeaknessesSvc.generateReport.bind(
					this._strengthsAndWeaknessesSvc,
				),
			},
		};
	}

	filters(type: LeaderReportType): Array<keyof LeaderReportFilters> {
		return this._REPORT_INITIALIZATION[type].filters;
	}

	generator(
		type: LeaderReportType,
	): (data: any, viewAll: boolean, ...args: any) => any {
		return this._REPORT_INITIALIZATION[type].generator;
	}
}
